import React, { useState } from 'react';
import { Modal, Button, Form, Spinner, ListGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { checkoutSale } from '../services/api';
import { formatCurrency } from '../utils/formatUtils';
import CurrencyInput from './CurrencyInput';

/**
 * Modal for a cashier to bill a PENDING_CASHIER sale.
 * The payment is registered in the cashier's own open register session.
 *
 * @param {boolean} show - Whether the modal is visible
 * @param {function} onHide - Callback to close the modal
 * @param {Object} sale - The sale to be checked out
 * @param {function} onSuccess - Callback invoked after a successful checkout
 */
const CheckoutModal = ({ show, onHide, sale, onSuccess }) => {
    const { t } = useTranslation();
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [paidAmount, setPaidAmount] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    const totalAmount = parseFloat(sale?.total_amount || 0);
    const alreadyPaid = parseFloat(sale?.paid_amount || 0);
    const remaining = totalAmount - alreadyPaid;

    // Pre-fill with the remaining amount whenever a new sale is loaded
    React.useEffect(() => {
        if (show && sale) {
            setPaidAmount(remaining > 0 ? remaining : 0);
            setPaymentMethod('cash');
        }
    }, [show, sale?.sale_id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (paidAmount <= 0) {
            toast.error(t('salesPage.checkoutModal.errors.amountPositive'));
            return;
        }

        setSubmitting(true);
        try {
            await checkoutSale(sale.sale_id, {
                payment_method: paymentMethod,
                paid_amount: paidAmount,
                payments_breakdown: [{ method: paymentMethod, amount: paidAmount }]
            });
            toast.success(t('salesPage.checkoutModal.success'));
            if (onSuccess) onSuccess();
            onHide();
        } catch (err) {
            const code = err.response?.data?.code;
            if (code === 'NO_OPEN_REGISTER') {
                toast.error(t('salesPage.checkoutModal.errors.noOpenRegister'));
            } else if (code === 'NOT_PENDING_CASHIER') {
                toast.error(t('salesPage.checkoutModal.errors.notPendingCashier'));
            } else {
                toast.error(err.response?.data?.message || t('salesPage.checkoutModal.errors.generic'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (!sale) return null;

    const formatMoney = (amount) => `$${formatCurrency(amount)}`;

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('salesPage.checkoutModal.title')}</Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {/* Sale summary */}
                    <div className="border rounded p-3 mb-3 bg-light">
                        <p className="mb-1">
                            <strong>{t('salesPage.checkoutModal.client')}:</strong>{' '}
                            {sale.client_name || <span className="text-muted">{t('salesPage.checkoutModal.noClientName')}</span>}
                        </p>
                        {sale.products?.length > 0 && (
                            <div className="mb-1">
                                <strong>{t('salesPage.checkoutModal.products')}:</strong>
                                <ListGroup variant="flush" className="mt-1">
                                    {sale.products.map((item, i) => (
                                        <ListGroup.Item key={i} className="px-0 py-1 small bg-transparent">
                                            {item.quantity}× {item.product_name}
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            </div>
                        )}
                        <div className="d-flex justify-content-between mt-2 pt-2 border-top">
                            <span className="text-muted">{t('salesPage.checkoutModal.total')}</span>
                            <strong>{formatMoney(totalAmount)}</strong>
                        </div>
                        {alreadyPaid > 0 && (
                            <div className="d-flex justify-content-between">
                                <span className="text-muted">{t('salesPage.checkoutModal.alreadyPaid')}</span>
                                <span className="text-success">{formatMoney(alreadyPaid)}</span>
                            </div>
                        )}
                        <div className="d-flex justify-content-between fw-bold">
                            <span>{t('salesPage.checkoutModal.remaining')}</span>
                            <span className="text-danger">{formatMoney(remaining)}</span>
                        </div>
                    </div>

                    {/* Payment form */}
                    <Form.Group className="mb-3">
                        <Form.Label>{t('salesPage.checkoutModal.paymentMethod')}</Form.Label>
                        <Form.Select
                            value={paymentMethod}
                            onChange={e => setPaymentMethod(e.target.value)}
                        >
                            <option value="cash">{t('salesPage.checkoutModal.methods.cash')}</option>
                            <option value="card">{t('salesPage.checkoutModal.methods.card')}</option>
                            <option value="transfer">{t('salesPage.checkoutModal.methods.transfer')}</option>
                            <option value="other">{t('salesPage.checkoutModal.methods.other')}</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>{t('salesPage.checkoutModal.amountToCharge')}</Form.Label>
                        <CurrencyInput
                            value={paidAmount}
                            onChange={(value) => setPaidAmount(value || 0)}
                            placeholder="0"
                            required
                        />
                        <Form.Text className="text-muted">
                            {t('salesPage.checkoutModal.max')}: {formatMoney(remaining)}
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={submitting}>
                        {t('salesPage.checkoutModal.cancel')}
                    </Button>
                    <Button variant="success" type="submit" disabled={submitting || paidAmount <= 0}>
                        {submitting
                            ? <><Spinner animation="border" size="sm" /> {t('salesPage.checkoutModal.processing')}</>
                            : t('salesPage.checkoutModal.confirm')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default CheckoutModal;
